import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import { CookieConsentBanner } from './CookieConsentBanner';

interface CookieConsentContextValue {
    isEnabled: boolean;
    hasConsent: boolean;
    analyticsConsent: boolean;
    marketingConsent: boolean;
    isLoading: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue>({
    isEnabled: false,
    hasConsent: false,
    analyticsConsent: false,
    marketingConsent: false,
    isLoading: true,
});

export const useCookieConsentContext = () => useContext(CookieConsentContext);

/**
 * Read impersonation state straight from storage rather than useAuth — this
 * runs inside a PostHog callback on every event, outside React's render cycle,
 * so it can't depend on context or on a captured value that may be stale.
 */
const isImpersonating = (): boolean => {
    try {
        const raw = localStorage.getItem('user');
        return !!raw && !!JSON.parse(raw)?.impersonation;
    } catch {
        return false;
    }
};

interface CookieConsentProviderProps {
    children: React.ReactNode;
    jwtToken?: string | null;
}

/**
 * Provider component that manages cookie consent and PostHog initialization.
 *
 * When cookie consent feature is DISABLED:
 * - PostHog runs normally (current behavior)
 * - No banner is shown
 *
 * When cookie consent feature is ENABLED:
 * - PostHog only initializes when analytics consent is given
 * - Banner is shown until user makes a choice
 */
export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({
    children,
    jwtToken,
}) => {
    const {
        isEnabled,
        isLoading,
        hasConsent,
        analyticsConsent,
        marketingConsent,
        acceptAll,
        rejectAll,
        savePreferences,
    } = useCookieConsent({ jwtToken });

    // PostHog configuration
    const posthogOptions = useMemo(
        () => ({
            api_host: process.env.REACT_APP_POSTHOG_PUBLIC_HOST,
            cross_subdomain_cookie: true,
            // Admin impersonation must never be recorded against the customer's
            // person — otherwise our own support clicks land in their timeline
            // and skew every funnel and retention number built on it. Dropped at
            // capture time rather than via opt_out_capturing(), which persists
            // and would leave analytics permanently off for that browser if the
            // opt-back-in were ever missed.
            before_send: (event: any) => (isImpersonating() ? null : event),
            sanitize_properties: (properties: Record<string, unknown>) => {
                return {
                    ...properties,
                    app_name: process.env.REACT_APP_POSTHOG_APP_NAME,
                };
            },
            // Don't auto-capture if we're in consent mode and no consent given
            autocapture: !isEnabled || analyticsConsent,
            capture_pageview: !isEnabled || analyticsConsent,
            capture_pageleave: !isEnabled || analyticsConsent,
        }),
        [isEnabled, analyticsConsent]
    );

    // Control PostHog based on consent
    useEffect(() => {
        if (!isEnabled) {
            // Feature disabled - PostHog runs normally
            return;
        }

        if (analyticsConsent) {
            // User consented - enable PostHog
            try {
                posthog.opt_in_capturing();
            } catch {
                // PostHog might not be initialized yet
            }
        } else if (hasConsent && !analyticsConsent) {
            // User explicitly rejected analytics - opt out
            try {
                posthog.opt_out_capturing();
            } catch {
                // PostHog might not be initialized yet
            }
        }
    }, [isEnabled, hasConsent, analyticsConsent]);

    const contextValue = useMemo(
        () => ({
            isEnabled,
            hasConsent,
            analyticsConsent,
            marketingConsent,
            isLoading,
        }),
        [isEnabled, hasConsent, analyticsConsent, marketingConsent, isLoading]
    );

    // Determine if we should initialize PostHog
    // Feature disabled OR (feature enabled AND analytics consent given)
    // ...and only with a real key: a blank REACT_APP_POSTHOG_PUBLIC_KEY is how
    // an environment opts out of analytics entirely (see .env.development).
    // Without this guard PostHogProvider initialises against an empty token.
    const posthogKey = process.env.REACT_APP_POSTHOG_PUBLIC_KEY;
    const shouldInitPostHog = (!isEnabled || analyticsConsent) && !!posthogKey;

    // Show banner when feature is enabled but no consent given yet
    const showBanner = isEnabled && !hasConsent && !isLoading;

    return (
        <CookieConsentContext.Provider value={contextValue}>
            {shouldInitPostHog ? (
                <PostHogProvider
                    apiKey={posthogKey!}
                    options={posthogOptions}
                >
                    {children}
                </PostHogProvider>
            ) : (
                // When consent not given, render children without PostHog
                <>{children}</>
            )}

            {/* Cookie consent banner */}
            {showBanner && (
                <CookieConsentBanner
                    onAcceptAll={acceptAll}
                    onRejectAll={rejectAll}
                    onSavePreferences={savePreferences}
                />
            )}
        </CookieConsentContext.Provider>
    );
};

export default CookieConsentProvider;
